/**
 * STAGE 2 — Async Thunks (createAsyncThunk)
 * -----------------------------------------
 * Redux Toolkit lets you write ASYNC logic (API calls) as named actions.
 *
 * Flow:
 *   dispatch(fetchAppData())  →  pending  →  API runs  →  fulfilled | rejected
 *
 * The slice listens with extraReducers and updates state when those complete.
 *
 * `condition` runs BEFORE the API — return false to skip (our "already loaded" guard).
 */
import { createAsyncThunk } from '@reduxjs/toolkit'
import urls from '../../../components/constants/apiurl'
import ApiCaller from '../../../components/constants/ApiCaller'

const parseJson = async (res) => {
  if (!res?.response) return null
  try {
    return await res.response.json()
  } catch {
    return null
  }
}

export const fetchAppData = createAsyncThunk(
  'common/fetchAppData',
  async ({ force = false } = {}, { getState, rejectWithValue }) => {
    const state = getState()
    const userData = state.userData || {}
    const token = userData.access_token || localStorage.getItem('userToken')
    const permissionId =
      localStorage.getItem('permissionId') || userData.roleId

    if (!token) {
      return rejectWithValue('Not logged in')
    }

    try {
      const permissionPromise = permissionId
        ? ApiCaller.get({}, `${urls.fetchPermission}/${permissionId}`, token)
        : Promise.resolve(null)

      const profilePromise = userData.id
        ? ApiCaller.get(
            {},
            `${urls.profileDetail}?id=${userData.id}`,
            token
          )
        : Promise.resolve(null)

      // Soft-fail wallet totals: 502/parse errors must not block permissions/profile
      const chipsPromise = ApiCaller.post(
        { walletType: 'CHIPS_WALLET' },
        urls.cashBeadsTotalAmount,
        token
      ).catch(() => null)

      const cashPromise = ApiCaller.post(
        { walletType: 'CASH_WALLET' },
        urls.cashBeadsTotalAmount,
        token
      ).catch(() => null)

      const [permRes, profileRes, chipsRes, cashRes] = await Promise.all([
        permissionPromise,
        profilePromise,
        chipsPromise,
        cashPromise
      ])

      const permJson = await parseJson(permRes)
      const profileJson = await parseJson(profileRes)
      const chipsJson = await parseJson(chipsRes)
      const cashJson = await parseJson(cashRes)

      return {
        permission:
          permJson?.status == 200 ? permJson.data : state.permission,
        profileImage:
          profileJson?.status == 200
            ? profileJson.data?.profileImage || ''
            : state.appData?.profileImage || '',
        totalChips:
          chipsJson?.status == 200 ? chipsJson.data : state.appData?.totalChips ?? 0,
        totalCash:
          cashJson?.status == 200 ? cashJson.data : state.appData?.totalCash ?? 0
      }
    } catch (error) {
      return rejectWithValue(error?.message || 'Failed to load app data')
    }
  },
  {
    // Skip duplicate fetches unless Login/Profile passes { force: true }
    condition: ({ force = false } = {}, { getState }) => {
      if (force) return true
      const { appData } = getState()
      return !appData?.isLoaded
    }
  }
)
